import React from 'react';
import Footer from '../components/Footer';
import './LegalPages.css';

function PrivacyPolicyPage() {
    return (
        <div className="container legal-page">
            <h1>🔐 Privacy Policy</h1>
            <p className="effective-date"><strong>Effective Date: 28 May 2025</strong></p>

            <p>This Privacy Policy explains how MythicMaps ("we", "us", or "our") collects, uses, and protects your personal data when you use our service. We are committed to protecting your privacy and complying with the UK GDPR and other applicable privacy laws.</p>

            <div className="privacy-highlight">
                <h3>🔒 Your Data Protection Guarantee</h3>
                <p><strong>We take your privacy seriously.</strong> Your personal data is encrypted, securely stored, and processed only for the purposes outlined in this policy. If you choose to delete your account, all your personal data will be completely and permanently destroyed from our systems.</p>
            </div>

            <h2>1. Who We Are</h2>
            <p>MythicMaps is a digital platform that allows users to create stylized maps from Strava activities. We are the <strong>data controller</strong> for the personal data we process.</p>
            <p>📧 Contact: <a href="mailto:data@mythicmaps.ai">data@mythicmaps.ai</a></p>

            <h2>2. What Personal Data We Collect</h2>

            <h3>a. Strava Activity Data</h3>
            <ul>
                <li><strong>What:</strong> Route GPS data (GPX tracks), activity name, date, type (hike/ride/run/etc.), distance, elevation gain, moving time, average speed, and activity-specific metrics (e.g., average watts for cycling).</li>
                <li><strong>Why:</strong> To generate customized stylized maps from your activities and display relevant activity statistics with your maps.</li>
                <li><strong>Source:</strong> You, via explicit consent through the Strava API.</li>
                <li><strong>Storage:</strong> Activity data is retained when you generate and save a map, allowing you to view statistics and regenerate maps later.</li>
            </ul>

            <h3>b. Strava Profile Data</h3>
            <ul>
                <li><strong>What:</strong> Name (first/last), username, bio, location (city, state, country), profile pictures, account creation date, premium status, gender, weight, follower/friend status.</li>
                <li><strong>Why:</strong> To personalize your experience, display your profile information, and enable social features within the app.</li>
                <li><strong>Source:</strong> Your Strava profile, accessed with your explicit consent.</li>
            </ul>

            <h3>c. Account Data</h3>
            <ul>
                <li><strong>What:</strong> Email address, authentication token, credits, saved preferences.</li>
                <li><strong>Why:</strong> To manage your account and enable credit tracking and support.</li>
            </ul>

            <h3>d. Usage Data</h3>
            <ul>
                <li><strong>What:</strong> Device type, browser, IP address, navigation actions.</li>
                <li><strong>Why:</strong> To protect the platform, debug issues, and improve UX.</li>
            </ul>

            <h3>e. Session Recordings</h3>
            <ul>
                <li><strong>What:</strong> Mouse movements, clicks, and scrolling behavior (anonymized).</li>
                <li><strong>Why:</strong> To improve the user experience through PostHog analytics.</li>
                <li><strong>Note:</strong> Sensitive fields (e.g., passwords, payment details) are automatically masked and never recorded.</li>
            </ul>

            <h3>f. Challenge Participation Data</h3>
            <ul>
                <li><strong>What:</strong> Display name, profile photo, challenge progress (cumulative distance, elevation gain, moving time), imported activity records (distance, date, moving time, elevation gain), join date, and completion status.</li>
                <li><strong>Why:</strong> To enable challenge leaderboards, progress tracking, and travelog features.</li>
                <li><strong>Public visibility:</strong> When you join a public challenge, your display name, profile photo, progress, and activity history are <strong>visible to all visitors</strong> of that challenge page, including non-participants and users who are not logged in.</li>
                <li><strong>Automatic imports:</strong> When you join a challenge, new Strava activities you record may be automatically imported into the challenge via Strava webhooks.</li>
                <li><strong>User control:</strong> You can set a challenge-specific display name and profile photo. You can also manage and remove individual activities from any challenge at any time.</li>
            </ul>

            <h2>3. Legal Basis for Processing</h2>
            <p>We process your data under the following GDPR bases:</p>
            <ul>
                <li><strong>Consent</strong> – for connecting to your Strava account and session replay.</li>
                <li><strong>Contract</strong> – to deliver the core functionality of the app.</li>
                <li><strong>Legitimate interest</strong> – to enhance our service via analytics and debugging.</li>
                <li><strong>Legal obligation</strong> – to comply with regulatory requirements.</li>
            </ul>

            <h2>4. Data Retention and Deletion</h2>
            <ul>
                <li><strong>Strava activity data:</strong>
                    <ul>
                        <li><strong>Temporary processing:</strong> Data accessed during map generation is processed temporarily and discarded if no map is generated.</li>
                        <li><strong>Saved maps:</strong> When you generate and save a map, the associated activity data (GPX track, statistics, name, date, type, etc.) is stored permanently with your map to enable viewing statistics and map regeneration.</li>
                        <li><strong>User control:</strong> You can delete individual saved maps and their associated activity data at any time from your account.</li>
                    </ul>
                </li>
                <li><strong>Strava profile data:</strong> Stored while your account is active to enable personalization and social features. Refreshed periodically from your Strava account to stay current.</li>
                <li><strong>Account data:</strong> Retained only while your account is active.</li>
                <li><strong>Challenge participation data:</strong> Retained while the challenge is active. You can remove individual activities at any time. If you leave a challenge or delete your account, all associated participation data is permanently destroyed.</li>
                <li><strong>Session recordings:</strong> Retained for up to 90 days for service improvement.</li>
                <li><strong>Complete account deletion:</strong> When you delete your account, <strong>all your personal data is permanently and irreversibly destroyed</strong> within 30 days. We do not retain any personal information after account deletion.</li>
                <li><strong>Legal retention:</strong> Only anonymized, non-personal usage statistics may be retained for legitimate business purposes after account deletion.</li>
            </ul>

            <h2>5. Sharing and Processors</h2>
            <p><strong>We never sell your data.</strong> We share data with carefully selected third-party processors only where necessary to provide our service:</p>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>Partner</th>
                        <th>Purpose</th>
                        <th>Privacy Policy</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Strava</td>
                        <td>OAuth sign-in & activity import</td>
                        <td><a href="https://www.strava.com/legal/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>OpenAI</td>
                        <td>AI content generation & processing</td>
                        <td><a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Google (Gemini)</td>
                        <td>AI content generation & processing</td>
                        <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Anthropic</td>
                        <td>AI content generation & processing</td>
                        <td><a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Mistral</td>
                        <td>AI content generation & processing</td>
                        <td><a href="https://mistral.ai/terms/#privacy-policy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Mapbox</td>
                        <td>Map tiles, static-image rendering, geocoding & terrain data</td>
                        <td><a href="https://www.mapbox.com/legal/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>PostHog</td>
                        <td>Product analytics & session replay</td>
                        <td><a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Firebase (Google)</td>
                        <td>Cloud hosting & database</td>
                        <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                    <tr>
                        <td>Resend</td>
                        <td>Transactional emails</td>
                        <td><a href="https://resend.com/privacy-policy" target="_blank" rel="noopener noreferrer">View Policy</a></td>
                    </tr>
                </tbody>
            </table>

            <p><strong>All processors:</strong></p>
            <ul>
                <li>Comply with GDPR via Standard Contractual Clauses (SCCs) or adequacy decisions</li>
                <li>Have undergone security and privacy assessments</li>
                <li>Process data only as instructed and for the specific purposes outlined</li>
                <li>Are required to delete data when instructed by us</li>
            </ul>

            <div className="privacy-highlight">
                <h3>🤖 Note on AI Processing</h3>
                <p>When we utilize AI providers (OpenAI, Google, Anthropic, Mistral), we only send the specific text prompts and activity context required to generate your content. We utilize commercial API agreements which ensure your data is not used to train their general public models.</p>
            </div>

            <h2>6. International Transfers</h2>
            <p>If we transfer data outside the UK or EEA, we ensure appropriate safeguards, including:</p>
            <ul>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Transfers to countries with adequacy decisions</li>
                <li>Additional security measures for US transfers</li>
            </ul>

            <h2>7. Analytics and Tracking</h2>
            <p>We use PostHog for product analytics and session recording to improve our service. This includes:</p>
            <ul>
                <li>Tracking how you use our features (anonymized)</li>
                <li>Recording user sessions with automatic masking of sensitive data</li>
                <li>Collecting performance metrics</li>
                <li>Understanding user behavior patterns for UX improvements</li>
            </ul>
            <p><strong>Privacy controls:</strong> You can opt out of analytics tracking in your account settings or by contacting us. Essential functionality will remain unaffected.</p>

            <h2>8. Data Minimization and User Control</h2>
            <p>We are committed to data minimization principles:</p>
            <ul>
                <li><strong>Purpose limitation:</strong> We only collect and use data for the specific purposes outlined in this policy.</li>
                <li><strong>Activity data control:</strong> You choose which activity data to retain by deciding which maps to save. Unsaved map generations result in temporary data processing only.</li>
                <li><strong>Map management:</strong> You can delete individual saved maps and their associated activity data at any time through your account dashboard.</li>
                <li><strong>Strava data control:</strong> You control what Strava data we can access through your Strava privacy settings. We only request the minimum permissions needed.</li>
                <li><strong>Profile visibility:</strong> You can control how your profile information is displayed within the app through your account settings.</li>
                <li><strong>Data refresh:</strong> Strava profile data is refreshed only when necessary to ensure accuracy - you can disconnect your Strava account at any time.</li>
                <li><strong>Location data:</strong> City, state, and country information from your Strava profile is used only for personalization and can be hidden in your settings.</li>
            </ul>

            <h2>9. Your Rights</h2>
            <p>Under GDPR, you have comprehensive rights over your data:</p>
            <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate information</li>
                <li><strong>Deletion:</strong> Complete permanent removal of all your data</li>
                <li><strong>Consent withdrawal:</strong> Revoke consent at any time</li>
                <li><strong>Data portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Object/Restrict:</strong> Limit certain types of processing</li>
                <li><strong>Complaint:</strong> Lodge complaints with the UK Information Commissioner's Office (ICO)</li>
            </ul>
            <p>To exercise these rights, email <a href="mailto:data@mythicmaps.ai">data@mythicmaps.ai</a>. We'll respond within 30 days.</p>

            <h2>10. Security Measures</h2>
            <p>We implement industry-standard security measures to protect your data:</p>
            <ul>
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Access controls:</strong> Strict role-based access with multi-factor authentication</li>
                <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
                <li><strong>Regular audits:</strong> Quarterly security assessments and penetration testing</li>
                <li><strong>Data minimization:</strong> We collect and retain only what's necessary</li>
            </ul>

            <h2>11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy occasionally. Major changes will be communicated via the site or email with 30 days notice. Continued use after changes constitutes acceptance.</p>

            <div className="contact-section">
                <p><strong>Questions or Concerns?</strong></p>
                <p>📧 <a href="mailto:data@mythicmaps.ai">data@mythicmaps.ai</a></p>
                <p>We're committed to transparency and will respond to all privacy inquiries promptly.</p>
            </div>
            <Footer />
        </div>
    );
}

export default PrivacyPolicyPage;

